import { Observable, Subject } from 'rxjs';

import { ComponentRef, Injectable, Injector, ComponentFactoryResolver, EmbeddedViewRef } from '@angular/core';

import { isComponentRoute, isTemplateRoute } from './utils';
import { SgAuxRouteBridgeOutlet, SgAuxRoute, SgAuxRouteBridgeHost } from './models';
import {
  SgAuxRouteBridgeEvent,
  SgAuxRouteBridgeCreateUpdateEvent,
  SgAuxRouterBridgeHostRouteChangedEvent,
} from './events';

@Injectable({
  providedIn: 'root',
})
export class SgAuxRouteBridgeService {
  readonly events: Observable<SgAuxRouteBridgeEvent>;

  private outlets = new Map<string, SgAuxRouteBridgeOutlet>();
  private activeHosts = new Map<string, SgAuxRouteBridgeHost>();
  private pathMap = new Map<string, SgAuxRoute>();
  private events$ = new Subject<SgAuxRouteBridgeEvent>();

  private pendingRouteEvents = new Map<SgAuxRouteBridgeHost, SgAuxRouterBridgeHostRouteChangedEvent>();

  constructor(private injector: Injector) {
    this.events = this.events$.asObservable();
  }

  isHostActive(outlet: string): boolean {
    return this.activeHosts.has(outlet);
  }

  getHostActivePath(outlet: string): string | undefined {
    if (this.isHostActive(outlet)) {
      return this.activeHosts.get(outlet).activePath;
    }
  }

  getOutlet(outlet: string): SgAuxRouteBridgeOutlet | undefined {
    return this.outlets.get(outlet);
  }

  findOutletByInstance(instance: any): string | undefined {
    const host = Array.from(this.activeHosts.values()).find(
      (h) => h.activeCmpRef && h.activeCmpRef.instance === instance
    );
    return host ? host.outlet : undefined;
  }

  getRoute<T = any>(path: string): SgAuxRoute<T> | undefined {
    return this.pathMap.get(path);
  }

  defineRoute<T>(config: SgAuxRoute<T>): void {
    this.pathMap.set(config.path, config);
    this.tryMatchLazyDefinitions(config.path);
  }

  registerOutlet(outlet: SgAuxRouteBridgeOutlet): void {
    if (this.outlets.has(outlet.name)) {
      throw new Error(`SgAuxRouteBridgeOutlet with the name ${outlet.name} already exists.`);
    }
    this.outlets.set(outlet.name, outlet);
    this.events$.next({ kind: 'register', outlet });
    outlet.onDestroy.subscribe(() => this.unregisterBridgeOutlet(outlet));
  }

  registerHost(bridge: SgAuxRouteBridgeHost): void {
    const { outlet } = bridge;
    // if (!this.outlets.has(outlet)) {
    //   throw new Error(`Can not find bridge outlet ${outlet}.`);
    // }
    if (this.activeHosts.has(outlet)) {
      throw new Error(`Bridge Host for outlet ${outlet} already exists.`);
    }

    this.activeHosts.set(outlet, bridge);
    bridge.routeChanged.subscribe((event) => this.handleRouteChanged(event));
    bridge.onDestroy.subscribe(() => this.unregisterBridge(outlet));
  }

  private handleRouteChanged(event: SgAuxRouterBridgeHostRouteChangedEvent): void {
    const { host } = event;

    const route = this.pathMap.get(event.path);
    if (route) {
      const eventData: SgAuxRouteBridgeCreateUpdateEvent = {
        kind: 'createOrUpdate',
        mode: event.pathChanged ? 'create' : 'update',
        outlet: host.outlet,
        path: event.path,
      };

      if (event.pathChanged) {
        this.createRouteView(route, host);
      }

      if (host.activeCmpRef) {
        eventData.cmpRef = host.activeCmpRef;
      } else {
        eventData.viewRef = host.activeViewRef;
      }
      this.events$.next(eventData);
    } else {
      // if not config found, save the event and try to detect late route configurations.
      // late route configuration are common when lazy loaded child  modules define routes.
      // we use a map with the host to keep one event per bridge.
      this.pendingRouteEvents.set(event.host, event);
    }
  }

  private tryMatchLazyDefinitions(path: string): void {
    if (this.pendingRouteEvents.size > 0) {
      for (const event of Array.from(this.pendingRouteEvents.values())) {
        if (path === event.path) {
          this.pendingRouteEvents.delete(event.host);
          this.handleRouteChanged(event);
          break;
        }
      }
    }
  }

  private createRouteView(route: SgAuxRoute<any>, host: SgAuxRouteBridgeHost) {
    const { path } = route;
    const { outlet } = host;

    let create: () => ComponentRef<any> | EmbeddedViewRef<any>;
    if (isComponentRoute(route)) {
      const injector = route.injector || this.injector;
      const cfr: ComponentFactoryResolver = injector.get(ComponentFactoryResolver);
      const cmpFactory = cfr.resolveComponentFactory(route.component);
      create = () => host.createComponent(cmpFactory);
    } else if (isTemplateRoute(route)) {
      create = () => host.createView(route.template);
    } else {
      throw new Error(`Invalid Aux route definition for path ${path}`);
    }
    this.runLifeCycleHook(outlet, 'auxBeforeCreateView');
    const ref = create();
    ref.onDestroy(() => {
      this.runLifeCycleHook(outlet, 'auxBeforeDestroyView');
      this.events$.next({ kind: 'destroy', outlet, path });
    });
  }

  private runLifeCycleHook(
    outlet: string,
    lifeCycle: 'auxBeforeCreateView' | 'auxBeforeDestroyView',
    ...args: any[]
  ): void {
    const outletInstance = this.getOutlet(outlet);
    const lchFn: any = outletInstance[lifeCycle];
    if (typeof lchFn === 'function') {
      lchFn.call(outletInstance, ...args);
    }
  }

  private unregisterBridgeOutlet(outlet: SgAuxRouteBridgeOutlet): void {
    const { name } = outlet;
    if (this.outlets.delete(name)) {
      this.unregisterBridge(name);
    }
  }

  private unregisterBridge(bridgeOrOutlet: SgAuxRouteBridgeHost | string): void {
    const outlet = typeof bridgeOrOutlet === 'string' ? bridgeOrOutlet : bridgeOrOutlet.outlet;
    const bridge = this.activeHosts.get(outlet);
    if (this.activeHosts.delete(outlet)) {
      this.events$.next({ kind: 'unregister', outlet });
      bridge.clearView();
    }
  }
}
